import styled, { css } from "styled-components";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import {
  NEW_LOOK_COMPACT_TABLE_CLASS,
  NEW_LOOK_PROGRESS_COLUMN_CLASS,
} from "features/trades/common/constants";

/**
 * Scopes the new-look table treatment to trades lists: the global micro
 * column headers on a hairline, ~56px rows with hairline separators, the
 * dashboard's row hover and a keyboard focus ring. `$flush` lines the first
 * and last columns up with a panel's 20px edges.
 */
const NewLookTradesTableWrapper = styled.div<{ $flush?: boolean }>`
  position: relative;
  /* Cells size their ellipsis and ladder by the table's width. */
  container: nl-trades / inline-size;

  .ant-table-wrapper .ant-table {
    background: transparent;
  }
  .ant-table-wrapper .ant-table-thead > tr > th,
  .ant-table-wrapper .ant-table-thead > tr > td {
    background: transparent !important;
    padding: 10px;
    white-space: nowrap;
  }
  .ant-table-wrapper .ant-table-thead th.ant-table-column-has-sorters:hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover} !important;
  }
  .ant-table-wrapper .ant-table-tbody > tr > td.ant-table-column-sort {
    background: transparent !important;
  }
  .ant-table-wrapper .ant-table-tbody > tr > td {
    height: 60px;
    padding: 10px;
    transition: background-color 120ms;
  }
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-row:hover > td,
  .ant-table-wrapper
    .ant-table-tbody
    .ant-table-row
    > .ant-table-cell-row-hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover} !important;
  }
  /* Selected rows read as selected (brand tint), over the shared table's
     near-white selection. */
  &&&
    .ant-table-wrapper
    .ant-table-tbody
    > tr.ant-table-row.ant-table-row-selected
    > td.ant-table-cell {
    background: ${(p) => p.theme.colors.lightBlue} !important;
  }
  .ant-table-wrapper .ant-table-tbody > tr.table-half-padding > td {
    padding: 10px !important;
  }
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-row:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: -2px;
  }
  .ant-table-wrapper .ant-table-cell.ant-table-selection-column {
    min-width: 44px;
    width: 44px;
  }
  /* Sorter icons sit right after the title; right-aligned (figure) columns
     keep their titles over the figures. */
  .ant-table-column-sorters {
    justify-content: flex-start;
    gap: 4px;
    .ant-table-column-title {
      flex: none;
    }
  }
  .ant-table-thead th.nl-end .ant-table-column-sorters {
    justify-content: flex-end;
  }
  .ant-table-pagination.ant-pagination {
    margin: 12px 0 4px;
  }
  .${NEW_LOOK_COMPACT_TABLE_CLASS} .ant-table-thead > tr > th,
  .${NEW_LOOK_COMPACT_TABLE_CLASS}
    .ant-table-tbody
    > tr.table-half-padding
    > td {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }
  .ant-table-wrapper
    .ant-table-thead
    > tr
    > th.${NEW_LOOK_PROGRESS_COLUMN_CLASS},
    .ant-table-wrapper
    .ant-table-tbody
    > tr
    > td.${NEW_LOOK_PROGRESS_COLUMN_CLASS} {
    padding-left: 32px !important;
  }
  .${NEW_LOOK_COMPACT_TABLE_CLASS}
    .ant-table-thead
    > tr
    > th.${NEW_LOOK_PROGRESS_COLUMN_CLASS},
    .${NEW_LOOK_COMPACT_TABLE_CLASS}
    .ant-table-tbody
    > tr
    > td.${NEW_LOOK_PROGRESS_COLUMN_CLASS} {
    padding-left: 24px !important;
  }
  ${(p) =>
    p.$flush
      ? css`
          .ant-table-wrapper .ant-table-thead > tr > th:first-child,
          .ant-table-wrapper .ant-table-tbody > tr > td:first-child {
            padding-left: 24px !important;
          }
          .ant-table-wrapper .ant-table-thead > tr > th:last-child,
          .ant-table-wrapper .ant-table-tbody > tr > td:last-child {
            padding-right: 24px !important;
          }
          .ant-table-pagination.ant-pagination {
            margin: 0;
            padding: 12px 24px;
            border-top: 1px solid ${getNewLookTokens(p.theme).hairline};
          }
        `
      : css`
          .ant-table-wrapper .ant-table-thead > tr > th:first-child,
          .ant-table-wrapper .ant-table-tbody > tr > td:first-child {
            padding-left: 8px !important;
          }
        `}
`;

export default NewLookTradesTableWrapper;
